import { addMonths, isAfter } from 'date-fns'
import { reaction } from 'mobx'
import { useLocalObservable } from 'mobx-react-lite'
import { useCallback, useContext, useEffect } from 'react'

import StoresContext from '@/utils/StoresContext'

export const LOOKING_FOR_AVAILABILITIES_MAX_MONTHS = 3
const LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS = 10

const useFetchNextAvailabilities = () => {
  const { appStore } = useContext(StoresContext)
  const {
    wish,
    canAutomaticallyChangeDate,
    isCurrentDayAvailableForCurrentPax,
    isCurrentDayFinished,
    selectedHighlightedOffer
  } = appStore.state

  const shouldComputeFirstAvailableDate = useCallback(
    () =>
      canAutomaticallyChangeDate &&
      (selectedHighlightedOffer
        ? !appStore.state.isSummaryDateCompatibleWithSelectedHighlightedOffer(wish.day)
        : !isCurrentDayAvailableForCurrentPax || isCurrentDayFinished),
    [
      appStore,
      canAutomaticallyChangeDate,
      isCurrentDayAvailableForCurrentPax,
      isCurrentDayFinished,
      selectedHighlightedOffer,
      wish.day
    ]
  )

  const localStore = useLocalObservable(() => ({
    hasLoadedNextAvailabilities: false,
    hasLoadedFirstAvailableDate: !shouldComputeFirstAvailableDate(),
    isLoadingFirstAvailableDate: shouldComputeFirstAvailableDate()
  }))

  const stopSearchingForFirstAvailableDate = useCallback(() => {
    if (!localStore.hasLoadedFirstAvailableDate) {
      localStore.isLoadingFirstAvailableDate = false
    }
  }, [localStore])

  const selectFirstAvailableDate = useCallback(
    (date) => {
      localStore.hasLoadedFirstAvailableDate = true
      appStore.selectWishDay(date).then(() => {
        localStore.isLoadingFirstAvailableDate = false
      })
    },
    [appStore, localStore]
  )

  useEffect(() => {
    const getNextAvailabilities = (next = 1) => {
      if (next >= LOOKING_FOR_AVAILABILITIES_MAX_MONTHS) {
        stopSearchingForFirstAvailableDate()
        return
      }
      const nextMonth = addMonths(wish.day, next).getMonth() + 1
      const yearOfNextMonth = addMonths(wish.day, next).getFullYear()
      appStore.getAvailabilitiesSummary(nextMonth, yearOfNextMonth).then(() => {
        if (appStore.state.daysAvailableForCurrentPax.length < LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS) {
          getNextAvailabilities(next + 1)
        } else {
          stopSearchingForFirstAvailableDate()
        }
      })
    }
    return reaction(
      () => [
        appStore.state.isLoading,
        appStore.state.isCurrentDayOpen,
        appStore.state.daysAvailableForCurrentPax.length
      ],
      () => {
        if (!appStore.state.isLoading && !appStore.state.isCurrentDayOpen && !appStore.state.currentMonth) {
          const [year, month] = appStore.state.currentMonthKey.split('-')
          appStore.getAvailabilitiesSummary(month, year)
        } else if (
          !appStore.state.isLoading &&
          appStore.state.daysAvailableForCurrentPax.length < LOOKING_FOR_AVAILABILITIES_MIN_OPEN_DAYS &&
          !localStore.hasLoadedNextAvailabilities
        ) {
          localStore.hasLoadedNextAvailabilities = true
          getNextAvailabilities()
        }
      },
      { fireImmediately: true }
    )
  }, [appStore, localStore, wish, stopSearchingForFirstAvailableDate])

  const nextAvailableDate = appStore.state.nextAvailableDateAtIndex(0)

  // effect to select first available date
  useEffect(() => {
    if (
      shouldComputeFirstAvailableDate() &&
      nextAvailableDate !== undefined &&
      !localStore.hasLoadedFirstAvailableDate
    ) {
      selectFirstAvailableDate(nextAvailableDate)
    }
  }, [
    localStore,
    nextAvailableDate,
    selectFirstAvailableDate,
    selectedHighlightedOffer,
    shouldComputeFirstAvailableDate
  ])

  // effect to fetch more availabilities if selected offer has a date_from in a month not loaded yet
  useEffect(() => {
    if (selectedHighlightedOffer && nextAvailableDate === undefined && shouldComputeFirstAvailableDate()) {
      const [year, month] = selectedHighlightedOffer.date_from.split('-')
      const startsAfterNow = isAfter(new Date(selectedHighlightedOffer.date_from), new Date())
      const isMonthLoaded = appStore.state.months.some((m) => m.key === `${year}-${month}`)
      if (startsAfterNow && !isMonthLoaded) {
        localStore.isLoadingFirstAvailableDate = true
        appStore.getAvailabilitiesSummary(month, year).then(() => {
          const newNextAvailableDate = appStore.state.nextAvailableDateAtIndex(0)
          if (newNextAvailableDate) {
            selectFirstAvailableDate(newNextAvailableDate)
          } else {
            stopSearchingForFirstAvailableDate()
          }
        })
      }
    }
  }, [
    appStore,
    localStore,
    nextAvailableDate,
    selectFirstAvailableDate,
    selectedHighlightedOffer,
    shouldComputeFirstAvailableDate,
    stopSearchingForFirstAvailableDate
  ])

  return {
    isLoadingFirstAvailableDate: localStore.isLoadingFirstAvailableDate,
    reloadFirstAvailableDate: () => {
      if (shouldComputeFirstAvailableDate()) {
        localStore.isLoadingFirstAvailableDate = true
        localStore.hasLoadedFirstAvailableDate = false
      }
    }
  }
}

export default useFetchNextAvailabilities
